import { Static, Type } from '@sinclair/typebox';

// ── Support chat (surcouche maison sur l'API cliente publique Chatwoot) ──
// UI de chat inline dans /support. Le backend proxifie l'API cliente Chatwoot
// (contact/conversations/messages) ; le HMAC d'identity validation ne quitte
// jamais le serveur. Le temps réel (réponses agent) passe par un socket
// ActionCable côté client, avec le `pubsub_token` renvoyé par /session.
// cf. docs/to_implement/chatwoot-support-integration-plan.md

export const SupportChatSessionSchema = Type.Object({
  // Token de souscription temps réel (ActionCable RoomChannel). Non-secret,
  // scopé au contact courant.
  pubsub_token: Type.String(),
  // Base URL Chatwoot pour ouvrir le socket `${realtime_url}/cable`.
  realtime_url: Type.String(),
});
export type SupportChatSessionObject = Static<typeof SupportChatSessionSchema>;

// ── Bornes du chat support ──────────────────────────────────────────────────
// Les endpoints d'envoi étant en `multipart/form-data` (pièces jointes), ils ne
// passent PAS par `@TypeBoxBody` : ces constantes sont la source de vérité unique,
// appliquées à la main côté backend (controller + limits multer) ET côté front
// (validation avant envoi + `maxLength` de la zone de saisie).
export const SUPPORT_MESSAGE_MAX_LENGTH = 5000;
export const SUPPORT_SUBJECT_MAX_LENGTH = 200;

// ── Pièces jointes (upload de fichiers dans le chat, comme Chatwoot classique) ──
// 10 Mo × 5 = 50 Mo bufferisés au maximum par requête : multer utilise
// `memoryStorage()` (le buffer est reforwardé à Chatwoot, jamais persisté), donc
// cette borne est directement de la RAM du pod backend — ne pas la remonter sans
// passer à un stockage disque/streaming.
export const SUPPORT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024; // 10 Mo par fichier
export const SUPPORT_ATTACHMENT_MAX_MB = SUPPORT_ATTACHMENT_MAX_BYTES / (1024 * 1024);
export const SUPPORT_ATTACHMENT_MAX_FILES = 5;
export const SUPPORT_ATTACHMENT_ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/webm',
  'audio/mp4',
] as const;

export const SupportAttachmentSchema = Type.Object({
  id: Type.Number(),
  // Catégorie Chatwoot : 'image' | 'audio' | 'video' | 'file'. Pilote le rendu front
  // (aperçu inline pour 'image', puce de téléchargement sinon).
  file_type: Type.String(),
  // `data_url` Chatwoot (URL publique du blob servi par l'instance Chatwoot).
  url: Type.String(),
  thumb_url: Type.Optional(Type.String()),
  filename: Type.Optional(Type.String()),
});
export type SupportAttachmentObject = Static<typeof SupportAttachmentSchema>;

export const SupportMessageSchema = Type.Object({
  message_id: Type.Number(),
  content: Type.String(),
  // 'me' = le pro (incoming), 'agent' = le support (outgoing), 'system' = activité.
  sender: Type.Union([Type.Literal('me'), Type.Literal('agent'), Type.Literal('system')]),
  created_at_formatted: Type.String(),
  // Nom/avatar de l'agent Chatwoot (messages 'agent' uniquement) : « vous parlez à X ».
  sender_name: Type.Optional(Type.String()),
  sender_avatar_url: Type.Optional(Type.String()),
  attachments: Type.Optional(Type.Array(SupportAttachmentSchema)),
});
export type SupportMessageObject = Static<typeof SupportMessageSchema>;

export const SupportConversationSchema = Type.Object({
  conversation_id: Type.Number(),
  status: Type.String(),
  // Sujet libre saisi par le pro à la création (stocké dans les custom_attributes
  // Chatwoot). Optionnel → fallback sur le dernier message pour l'affichage.
  subject: Type.Optional(Type.String()),
  last_message: Type.Optional(Type.String()),
  updated_at_formatted: Type.Optional(Type.String()),
  // Auteur du dernier message du fil : pilote le préfixe « Vous : » de l'aperçu.
  last_message_sender: Type.Optional(Type.Union([Type.Literal('me'), Type.Literal('agent'), Type.Literal('system')])),
  // Messages du support arrivés depuis le dernier passage du pro sur ce fil. Dérivé de
  // `contact_last_seen_at`, le repère de lecture porté par CHATWOOT lui-même (avancé via
  // `POST …/conversations/{id}/update_last_seen`) : le compteur est donc le même sur tous
  // les appareils du pro, contrairement à un repère stocké dans le navigateur.
  unread_count: Type.Optional(Type.Number()),
  // Statut de la suggestion Pulse (badge « Suggestion à lire » dans la liste). Absent
  // pour une demande antérieure au triage automatique.
  pulse_status: Type.Optional(Type.String()),
});
export type SupportConversationObject = Static<typeof SupportConversationSchema>;

// Une conversation est « en cours » tant qu'elle n'est pas résolue (Chatwoot :
// open / pending / snoozed). Sert au tri de la liste (non résolues en haut) côté
// backend et au regroupement visuel côté front — une seule définition pour les deux.
export function isSupportConversationOngoing(status?: string): boolean {
  return status !== 'resolved';
}

export const SupportConversationListSchema = Type.Object({
  conversations: Type.Array(SupportConversationSchema),
});
export type SupportConversationListObject = Static<typeof SupportConversationListSchema>;

export const SupportMessageListSchema = Type.Object({
  conversation_id: Type.Number(),
  messages: Type.Array(SupportMessageSchema),
});
export type SupportMessageListObject = Static<typeof SupportMessageListSchema>;

// Pas de schéma de requête ici : l'envoi de message et la création de conversation
// sont en `multipart/form-data` (pièces jointes), donc hors `@TypeBoxBody`. Les bornes
// sont portées par `SUPPORT_MESSAGE_MAX_LENGTH` / `SUPPORT_SUBJECT_MAX_LENGTH` ci-dessus
// et appliquées explicitement dans `SupportChatController`.

// ── Triage automatique des demandes + suggestion Pulse ──────────────────────────
// cf. docs/to_implement/support-triage-pulse-plan.md. Les enums sont DUAL-DEFINED
// (schema.prisma + ici) : même contenu, même ordre. Les labels Chatwoot sont dérivés
// mécaniquement (kebab-case, Chatwoot n'accepte ni espace ni majuscule).

/** Intention d'un ticket : exactement une par ticket. */
export enum SupportTicketIntent {
  QUESTION = 'QUESTION',
  BUG = 'BUG',
  EVOLUTION = 'EVOLUTION',
  FACTURATION = 'FACTURATION',
  COMPTE = 'COMPTE',
  COMMERCIAL = 'COMMERCIAL',
  AUTRE = 'AUTRE',
}

/** Domaine produit concerné : au plus un par ticket. */
export enum SupportTicketArea {
  AGENDA = 'AGENDA',
  RESERVATION = 'RESERVATION',
  CLIENTS = 'CLIENTS',
  CATALOGUE = 'CATALOGUE',
  SITE_WEB = 'SITE_WEB',
  DOMAINE_EMAIL = 'DOMAINE_EMAIL',
  PAIEMENT_CLIENT = 'PAIEMENT_CLIENT',
  NOTIFICATIONS = 'NOTIFICATIONS',
  MOBILE = 'MOBILE',
  MIGRATION_V1 = 'MIGRATION_V1',
  PULSE = 'PULSE',
  AUTRE = 'AUTRE',
}

/** Mappée sur la priorité NATIVE Chatwoot (`toggle_priority`), pas un label. */
export enum SupportTicketPriority {
  URGENT = 'URGENT',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

/**
 * Sous-qualification d'un BUG. `PROBABLE_CONFIG` = le symptôme décrit correspond
 * à un réglage manquant ou mal fait (disponibilités non ouvertes, prestation non
 * publiée, DNS non propagé…) : Pulse peut expliquer quoi vérifier. `DEFECT` = vrai
 * défaut probable, humain uniquement.
 */
export enum SupportBugKind {
  DEFECT = 'DEFECT',
  PROBABLE_CONFIG = 'PROBABLE_CONFIG',
}

/** Cycle de vie de la suggestion Pulse sur un ticket. */
export enum SupportPulseStatus {
  /** Ticket antérieur à la feature, ou triage désactivé. */
  NONE = 'NONE',
  /** Job de triage en cours (le front attend). */
  PENDING = 'PENDING',
  /** Suggestion affichée au pro, décision attendue. */
  SUGGESTED = 'SUGGESTED',
  /** Le pro a accepté : conversation résolue, jamais vue par un humain. */
  ACCEPTED = 'ACCEPTED',
  /** Le pro a refusé d'un clic : le support prend le relais. */
  REJECTED = 'REJECTED',
  /** Le pro a répondu dans le fil sans trancher : même effet qu'un refus. */
  REJECTED_IMPLICIT = 'REJECTED_IMPLICIT',
  /** Triage fait, rien proposé (non éligible, marker [[SUPPORT]], juge sous le seuil). */
  SKIPPED = 'SKIPPED',
  /** Le job a échoué avant de conclure. */
  FAILED = 'FAILED',
}

/** Raison pour laquelle aucune suggestion n'a été montrée (`pulse_skip_reason`). */
export enum SupportPulseSkipReason {
  NOT_ELIGIBLE = 'not_eligible',
  MARKER_SUPPORT = 'marker_support',
  JUDGE_BELOW_THRESHOLD = 'judge_below_threshold',
  /** Pulse a répondu par une question de clarification ou un texte trop court. */
  CLARIFICATION = 'clarification',
  /** Pulse a promis une action ou émis un appel d'outil (impossible en différé). */
  ACTION_PROMISED = 'action_promised',
  /** Budget temps du job épuisé avant la fin. */
  BUDGET_EXCEEDED = 'budget_exceeded',
  LLM_ERROR = 'llm_error',
  DISABLED = 'disabled',
  /** Le pro ou un agent a écrit dans le fil pendant le calcul : la suggestion arriverait hors sujet. */
  SUPERSEDED = 'superseded',
  /** Ticket créé par un admin en impersonation : aucun tour Pulse au nom du pro. */
  IMPERSONATION = 'impersonation',
}

/**
 * Où en est le pro dans sa vie client au moment du ticket — calculé en base, jamais
 * par le LLM. `ESSAI` et `NOUVEAU` = fenêtre où le churn est le plus fort : priorité
 * Chatwoot relevée au moins à HIGH (cf. `supportPriorityForSegment`).
 */
export enum SupportCustomerSegment {
  /** Aucun abonnement (essai en cours ou expiré sans conversion). */
  ESSAI = 'ESSAI',
  /** Abonné depuis moins de `SUPPORT_NEW_CUSTOMER_WINDOW_DAYS` (migrés v1 inclus : l'horloge part de la bascule). */
  NOUVEAU = 'NOUVEAU',
  ETABLI = 'ETABLI',
}

export const SUPPORT_NEW_CUSTOMER_WINDOW_DAYS = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `subscriptionCreatedAt` = création de la ligne `pro_subscription` (null = jamais
 * abonné). Elle survit à la résiliation et vaut la date de bascule pour un pro migré :
 * un migré récent est donc `NOUVEAU`, comme voulu (la bascule est une période à risque).
 */
export function resolveSupportCustomerSegment(
  subscriptionCreatedAt: Date | null,
  now: Date = new Date(),
): SupportCustomerSegment {
  if (subscriptionCreatedAt == null) return SupportCustomerSegment.ESSAI;
  const ageMs = now.getTime() - subscriptionCreatedAt.getTime();
  return ageMs < SUPPORT_NEW_CUSTOMER_WINDOW_DAYS * DAY_MS
    ? SupportCustomerSegment.NOUVEAU
    : SupportCustomerSegment.ETABLI;
}

const SUPPORT_PRIORITY_RANK: Record<SupportTicketPriority, number> = {
  [SupportTicketPriority.LOW]: 0,
  [SupportTicketPriority.MEDIUM]: 1,
  [SupportTicketPriority.HIGH]: 2,
  [SupportTicketPriority.URGENT]: 3,
};

/**
 * Priorité posée dans Chatwoot : plancher HIGH pour les segments à risque de churn,
 * URGENT (décidé par le LLM) conservé. La priorité du LLM reste seule en base.
 */
export function supportPriorityForSegment(
  priority: SupportTicketPriority,
  segment: SupportCustomerSegment,
): SupportTicketPriority {
  if (segment === SupportCustomerSegment.ETABLI) return priority;
  return SUPPORT_PRIORITY_RANK[priority] >= SUPPORT_PRIORITY_RANK[SupportTicketPriority.HIGH]
    ? priority
    : SupportTicketPriority.HIGH;
}

// ── Labels Chatwoot ─────────────────────────────────────────────────────────────

const toChatwootLabelValue = (value: string): string => value.toLowerCase().replace(/_/g, '-');

export const SUPPORT_LABEL_PREFIX_INTENT = 'intent-';
export const SUPPORT_LABEL_PREFIX_AREA = 'area-';
export const SUPPORT_LABEL_BUG_CONFIG = 'bug-config';
export const SUPPORT_LABEL_PULSE_SUGGESTED = 'pulse-suggere';
export const SUPPORT_LABEL_PULSE_ACCEPTED = 'pulse-accepte';
export const SUPPORT_LABEL_PULSE_REJECTED = 'pulse-refuse';
export const SUPPORT_LABEL_PULSE_NONE = 'pulse-sans-suggestion';

export function supportIntentLabel(intent: SupportTicketIntent): string {
  return `${SUPPORT_LABEL_PREFIX_INTENT}${toChatwootLabelValue(intent)}`;
}

export function supportAreaLabel(area: SupportTicketArea): string {
  return `${SUPPORT_LABEL_PREFIX_AREA}${toChatwootLabelValue(area)}`;
}

/** Labels de cycle de vie Pulse : mutuellement exclusifs sur une conversation. */
export const SUPPORT_PULSE_LIFECYCLE_LABELS = [
  SUPPORT_LABEL_PULSE_SUGGESTED,
  SUPPORT_LABEL_PULSE_ACCEPTED,
  SUPPORT_LABEL_PULSE_REJECTED,
  SUPPORT_LABEL_PULSE_NONE,
] as const;

export const SUPPORT_LABEL_CUSTOMER_TRIAL = 'client-essai';
export const SUPPORT_LABEL_CUSTOMER_NEW = 'client-nouveau';

/** Labels de segment client : mutuellement exclusifs, aucun pour `ETABLI`. */
export const SUPPORT_CUSTOMER_SEGMENT_LABELS = [SUPPORT_LABEL_CUSTOMER_TRIAL, SUPPORT_LABEL_CUSTOMER_NEW] as const;

export function supportCustomerSegmentLabel(segment: SupportCustomerSegment): string | null {
  switch (segment) {
    case SupportCustomerSegment.ESSAI:
      return SUPPORT_LABEL_CUSTOMER_TRIAL;
    case SupportCustomerSegment.NOUVEAU:
      return SUPPORT_LABEL_CUSTOMER_NEW;
    default:
      return null;
  }
}

/**
 * Liste plate de TOUS les labels que le triage peut poser. Source du script de
 * provisioning Chatwoot et du test « aucun enum orphelin ».
 */
export const SUPPORT_CHATWOOT_LABELS: readonly string[] = [
  ...Object.values(SupportTicketIntent).map(supportIntentLabel),
  ...Object.values(SupportTicketArea).map(supportAreaLabel),
  SUPPORT_LABEL_BUG_CONFIG,
  ...SUPPORT_PULSE_LIFECYCLE_LABELS,
  ...SUPPORT_CUSTOMER_SEGMENT_LABELS,
];

// ── Sortie du classifieur (contrat JSON strict, validé TypeBox côté backend) ────

export const SUPPORT_TRIAGE_SUMMARY_MAX_LENGTH = 200;

export const SupportTicketTriageResultSchema = Type.Object({
  intent: Type.Enum(SupportTicketIntent),
  area: Type.Union([Type.Enum(SupportTicketArea), Type.Null()]),
  priority: Type.Enum(SupportTicketPriority),
  confidence: Type.Number({ minimum: 0, maximum: 1 }),
  summary: Type.String({ maxLength: SUPPORT_TRIAGE_SUMMARY_MAX_LENGTH }),
  needs_human: Type.Boolean(),
  bug_kind: Type.Union([Type.Enum(SupportBugKind), Type.Null()]),
  language: Type.String(),
});
export type SupportTicketTriageResultObject = Static<typeof SupportTicketTriageResultSchema>;

// ── Éligibilité à la suggestion Pulse — fonction pure, une seule définition ─────

export const SUPPORT_PULSE_MIN_CONFIDENCE_QUESTION = 0.6;
export const SUPPORT_PULSE_MIN_CONFIDENCE_BUG_CONFIG = 0.7;
export const SUPPORT_PULSE_MIN_DESCRIPTION_LENGTH = 15;

/**
 * Vrai ssi le ticket peut recevoir une suggestion Pulse : une QUESTION assez sûre,
 * ou un BUG jugé « problème de configuration probable », sans signal « humain
 * requis », avec une demande exploitable (objet + description : un objet explicite
 * compense une description courte). Le flag d'activation est vérifié par l'appelant
 * (env backend), pas ici.
 */
export function isPulseEligible(
  triage: Pick<SupportTicketTriageResultObject, 'intent' | 'confidence' | 'needs_human' | 'bug_kind'>,
  description: string,
  subject = '',
): boolean {
  if (triage.needs_human) return false;
  if (`${subject}${description}`.replace(/\s+/g, '').length < SUPPORT_PULSE_MIN_DESCRIPTION_LENGTH) return false;
  if (triage.intent === SupportTicketIntent.QUESTION) {
    return triage.confidence >= SUPPORT_PULSE_MIN_CONFIDENCE_QUESTION;
  }
  if (triage.intent === SupportTicketIntent.BUG && triage.bug_kind === SupportBugKind.PROBABLE_CONFIG) {
    return triage.confidence >= SUPPORT_PULSE_MIN_CONFIDENCE_BUG_CONFIG;
  }
  return false;
}

// ── API pro : état de la suggestion + décision ─────────────────────────────────

export const SupportAssistSchema = Type.Object({
  status: Type.Enum(SupportPulseStatus),
  intent: Type.Optional(Type.Enum(SupportTicketIntent)),
  /** Réponse Pulse (Markdown), présente dès SUGGESTED et conservée après décision. */
  answer: Type.Optional(Type.String()),
  answered_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'answered_at', with_time: true } }),
  ),
  decided_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'decided_at', with_time: true } }),
  ),
});
export type SupportAssistObject = Static<typeof SupportAssistSchema>;

export enum SupportAssistDecision {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
}

export const SupportAssistDecisionSchema = Type.Object({
  decision: Type.Enum(SupportAssistDecision),
});
export type SupportAssistDecisionObject = Static<typeof SupportAssistDecisionSchema>;

/** Statuts terminaux : le front arrête de poller dès qu'il en voit un. */
export function isSupportPulseStatusTerminal(status: SupportPulseStatus): boolean {
  return status !== SupportPulseStatus.PENDING;
}
